import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import SelectionButton from '~/bootcamp/Activities/components/SelectionButton'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionTypes2 from './useFractionTypes2'

// The emit must not carry a raw i18n key.
const TYPE_PHRASE: { [key: string]: string } = {
  properFraction: 'the proper fraction',
  improperFraction: 'the improper fraction',
  mixedFraction: 'the mixed number',
}

interface FractionPropsModel {
  numerator: number
  denominator: number
  quotient: number
  dividerClassName?: string
}

function Fraction({ numerator, denominator, quotient, dividerClassName = '' }: FractionPropsModel): React.ReactElement {
  return (
    <div className="flex items-center space-x-1">
      {!!quotient && <span className="text-2xl">{quotient}</span>}
      <div>
        <span className="text-2xl">{numerator}</span>
        <Divider className={dividerClassName} />
        <span className="text-2xl">{denominator}</span>
      </div>
    </div>
  )
}

function FractionTypes2({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const {
    fractions,
    fractionTypeToSelect,
    formValid,
    selectedAnswer,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useFractionTypes2({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })

  const { t } = useTranslation('bootcamp')
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  // Each option's own `type` (the verdict) never rides the emit.
  const optionNumbers = fractions.map(
    ({ numerator, denominator, quotient }) => `${quotient ? `${quotient} ` : ''}\\frac{${numerator}}{${denominator}}`,
  )
  useEmitInstance(
    onInstanceChange,
    TYPE_PHRASE[fractionTypeToSelect] && optionNumbers.length
      ? `pick ${TYPE_PHRASE[fractionTypeToSelect]}; options: ${optionNumbers.join(', ')}`
      : null,
  )

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>{t(`fractions.${fractionTypeToSelect}`)}</FractionsActivityWrapper>
      <div className="flex justify-around space-x-2 sm:space-x-8 w-full">
        {fractions.map(({ numerator, denominator, quotient, type }) => (
          <SelectionButton
            className="p-2 flex-1 flex justify-center"
            isSelected={selectedAnswer === type}
            isWrong={selectedAnswer === type && answerState === false}
            key={type}
            onClick={onInputChange.bind(null, type)}
          >
            <Fraction
              denominator={denominator}
              dividerClassName={selectedAnswer === type ? 'bg-white' : ''}
              numerator={numerator}
              quotient={quotient}
            />
          </SelectionButton>
        ))}
      </div>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionTypes2
